// TimeSeriesRenderer.tsx
// 共通のMultiSeriesTimeSeriesPlotコンポーネントを使用するレンダラー

import { GraphRenderer, GraphCallbacks, TimeSeriesConfig } from '../../../../types/graph-builder.types';
import { DataSet } from '../../../../types';
import { getColumnById } from '../../../utils/column-utils';
import { MultiSeriesTimeSeriesPlot } from '../../../components/MultiSeriesTimeSeriesPlot';

export class TimeSeriesRenderer implements GraphRenderer<TimeSeriesConfig> {
  validate(config: TimeSeriesConfig, dataset: DataSet): string | null {
    if (!config.xColumn) {
      return 'X-axis column is required';
    }
    
    // Y軸カラムまたは範囲のいずれかが必要
    const hasYColumns = config.yColumns && config.yColumns.length > 0;
    const hasRanges = config.rangeColumns && 
      config.rangeColumns.some(r => r.upperColumn && r.lowerColumn);
    
    if (!hasYColumns && !hasRanges) {
      return 'At least one Y-axis column or range band is required';
    }
    
    // X軸のカラムが時系列データとして解釈可能かチェック
    const xColumn = getColumnById(dataset, config.xColumn);
    if (!xColumn) {
      return 'Selected X-axis column not found';
    }
    
    // date型、datetime型、数値型、もしくは解析可能な文字列であることを確認
    if (xColumn.type !== 'date' && xColumn.type !== 'datetime' && 
        xColumn.type !== 'int64' && xColumn.type !== 'float64' && 
        xColumn.type !== 'string') {
      return 'X-axis column must be date, datetime, numeric (int64/float64) or string type';
    }
    
    // Y軸のカラムが数値型であることを確認（Y軸カラムがある場合のみ）
    if (config.yColumns && config.yColumns.length > 0) {
      for (const yCol of config.yColumns) {
        const column = getColumnById(dataset, yCol);
        if (!column) {
          return `Y-axis column "${yCol}" not found`;
        }
        if (column.type !== 'int64' && column.type !== 'float64') {
          return `Y-axis column "${yCol}" must be numeric`;
        }
      }
    }
    
    // 範囲カラムの検証
    if (config.rangeColumns) {
      for (const rangeConfig of config.rangeColumns) {
        
        // 上限カラムの検証
        const upperColumn = getColumnById(dataset, rangeConfig.upperColumn);
        if (!upperColumn) {
          return `Upper bound column "${rangeConfig.upperColumn}" not found`;
        }
        if (upperColumn.type !== 'int64' && upperColumn.type !== 'float64') {
          return `Upper bound column "${rangeConfig.upperColumn}" must be numeric`;
        }
        
        // 下限カラムの検証
        const lowerColumn = getColumnById(dataset, rangeConfig.lowerColumn);
        if (!lowerColumn) {
          return `Lower bound column "${rangeConfig.lowerColumn}" not found`;
        }
        if (lowerColumn.type !== 'int64' && lowerColumn.type !== 'float64') {
          return `Lower bound column "${rangeConfig.lowerColumn}" must be numeric`;
        }
      }
    }
    
    return null;
  }

  render(config: TimeSeriesConfig, dataset: DataSet, callbacks: GraphCallbacks) {
    // xColumnのバリデーション
    const xColumn = getColumnById(dataset, config.xColumn);

    if (!xColumn) {
      return <div>Error: X-axis column not found</div>;
    }


    // onPointClickハンドラーを作成
    const handlePointClick = callbacks.onElementSelect 
      ? (dataIndices: number[], isAdditive?: boolean) => {
          callbacks.onElementSelect!(dataIndices, isAdditive || false);
        }
      : undefined;

    // MultiSeriesTimeSeriesPlotを使用（複数のY軸系列をサポート）
    return (
      <MultiSeriesTimeSeriesPlot
        dataset={dataset}
        dateColumn={config.xColumn}
        valueColumns={config.yColumns}
        rangeColumns={config.rangeColumns}
        rangeOpacity={config.rangeOpacity}
        selectedRows={callbacks.selectedRows}
        onPointClick={handlePointClick}
        width={undefined} // コンテナに合わせて自動調整
        height={undefined}
      />
    );
  }
}